import { useMutation, useQuery } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { StatusChip } from "components/StatusChip";
import {
  EXPORT_STAFF_CERS_UNMATCHED_EXPANSION,
  GET_STAFF_CERS_UNMATCHED_EXPANSION,
} from "hooks/useStaffCersUnmatchedExpansion";
import { useCallback, useMemo } from "react";
import { downloadFromUrl } from "util/download";

import { buildExpansionTableRows } from "./coverageRows";

type ExpansionRow = ReturnType<typeof buildExpansionTableRows>[number];

// Alerts + download fire from onCompleted rather than effects keyed on the
// mutation's data: Apollo doesn't clear `data` between calls, so a repeat
// outcome (second success, same errorMsg) never re-triggers an effect.
function ExportButton() {
  const alerts = useAlerts();
  const [exportMutation, { loading }] = useMutation(EXPORT_STAFF_CERS_UNMATCHED_EXPANSION, {
    onCompleted: (data) => {
      const r = data.exportStaffCersUnmatchedExpansion;
      if (r?.success) alerts.success("Export ready");
      else if (r?.errorMsg) alerts.error(`Export failed: ${r.errorMsg}`);
      if (r?.downloadUrl) downloadFromUrl(r.downloadUrl);
    },
    onError: (err) => alerts.error(`Export failed: ${err.message}`),
  });
  const onClick = useCallback(() => exportMutation(), [exportMutation]);
  return (
    <Button
      size="medium"
      loading={loading}
      startIcon={<FileDownload />}
      onClick={onClick}
      aria-label="Export facility list"
    >
      Export facility list
    </Button>
  );
}

// Deep-link to the Facilities tab, scoped to this business's unmatched rows.
// Unmatched facilities have no tenant, so we scope by the CERS business id (the
// Facilities grid supports both `matchStatus` and `cersBusinessId` omnisearch
// tokens); this lands the user on exactly the facilities behind the clicked row.
function facilitiesHref(cersBusinessId: string): string {
  const omni = `matchStatus:UNMATCHED cersBusinessId:${cersBusinessId}`;
  return `/staff/cers/facilities?omnisearch=${encodeURIComponent(omni)}`;
}

export function UnmatchedExpansionSection() {
  const { data, loading } = useQuery(GET_STAFF_CERS_UNMATCHED_EXPANSION, {
    fetchPolicy: "cache-and-network",
  });
  const result = data?.staffCersUnmatchedExpansion;
  const rows = useMemo<ExpansionRow[]>(
    () => (result ? buildExpansionTableRows(result.byTenant, result.stats) : []),
    [result]
  );

  const columns: CustomGridColDef<ExpansionRow>[] = useMemo(
    () => [
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "tenant" ? (
            row.tenantName
          ) : (
            <Typography variant="body2" color="text.secondary">
              Net-new / unattributed
            </Typography>
          ),
      },
      {
        field: "business",
        headerName: "CERS business",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "tenant" ? (
            (row.businessName ?? "—")
          ) : (
            <Typography variant="body2" color="text.secondary">
              {row.businessCount} CERS {row.businessCount === 1 ? "business" : "businesses"}, no
              matched sibling
            </Typography>
          ),
      },
      {
        field: "unmatchedCount",
        headerName: "Unmatched facilities",
        width: 190,
        sortable: false,
        renderCell: ({ row }) => (
          <StatusChip label={`${row.unmatchedCount} not in Encamp`} color="warning" />
        ),
      },
    ],
    []
  );

  const getRowLink = useCallback(
    (row: ExpansionRow) => (row.kind === "tenant" ? facilitiesHref(row.cersBusinessId) : null),
    []
  );

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6">Unmatched expansion</Typography>
        <ExportButton />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        CERS facilities that aren’t in Encamp, grouped by the customer they belong to (via matched
        siblings) — an expansion signal for CS. Export the full list, or open a customer in
        Facilities to match or ignore its facilities.
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row: ExpansionRow) => row.id}
        loading={loading}
        noRowsMessage="No unmatched facilities."
        getRowLink={getRowLink}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnMenu
      />
    </Paper>
  );
}
